<template>
    <div class="gift-landing-page min-h-screen bg-gray-50">
        <main role="main">
            <!-- Hero Section -->
            <section class="py-12 bg-blue-dark text-white" aria-labelledby="hero-headline">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-8">
                    <div class="flex justify-center md:justify-start basis-1/3">
                        <img class="max-w-xs p-6" :src="$asset('/hkcm/images/present.png', 'public')" alt="HKCM Present" title="HKCM Present" />
                    </div>

                    <div class="basis-2/3 text-center md:text-left">
                        <h1>{{ texts.hero.headline }}</h1>

                        <p class="max-w-xl mb-4 md:mx-0 mx-auto">
                            {{ texts.hero.subcopy }}
                        </p>

                        <div class="flex md:justify-start justify-center items-center gap-4 mt-6 lg:mt-8 flex-wrap">
                            <button type="button" class="btn primary flex flex-row gap-2 items-center" @click="scrollToConfigurator">
                                {{ texts.hero.cta }}
                                <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </button>

                            <button type="button" class="btn secondary flex flex-row gap-2 items-center max-w-3xl" @click="showRedeemPopup = true">
                                Geschenkcode einlösen
                                <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            <!-- Konfigurator Section -->
            <section ref="configuratorSection" class="py-12 sm:py-16 lg:py-20" aria-labelledby="configurator-headline">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <!-- Section Header -->
                    <header class="text-center mb-10 sm:mb-12">
                        <h2 id="configurator-headline" class="text-2xl">
                            {{ texts.configurator.headline }}
                        </h2>
                        <p class="">
                            {{ texts.configurator.subcopy }}
                        </p>
                    </header>

                    <!-- Product Cards -->
                    <Loading v-if="!showLoginButton" :loading="loading" :preserve-height="true" class="h-full">
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                            <div v-for="(productCategory, index) in productCategories()" :key="productCategory.id" ref="box" class="w-fit relative shrink category">
                                <div class="rounded-lg py-2">
                                    <div class="flex cursor-default select-none">
                                        <ProductCategorySingleMid :key="productCategory.id" :data="productCategory" class="bg-white" />
                                    </div>
                                    <div class="bg-white mt-4 rounded-xl shadow-md">
                                        <div class="grid grid-cols-1 box-inner place-items-center">
                                            <ProductGroupDirect
                                                v-for="(product_group, index) in getProductGroupByProductCategoryId(productCategory.id)?.data.data"
                                                :key="product_group.id"
                                                ref="productGroupDirect"
                                                :data="product_group"
                                                :product-category="productCategory"
                                                :first="index === 0"
                                                class="transition-all ease-in-out duration-300 w-full"
                                                :gift-sub="true"
                                                @shrink-other="shrink"
                                                @gift-sub-selected="handleProductSelect"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Loading>
                    <div v-if="showLoginButton" class="max-w-2xl mx-auto border border-blue-dark rounded-lg p-6 text-center" role="alert">
                        <svg class="w-12 h-12 text-blue-dark mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <p class="text-blue-dark font-medium mb-4">Bitte melden Sie sich an, um Geschenke zu kaufen.</p>
                        <Login class="text-left" redirect-to="/geschenk" @login-success="showLoginButton = false" />
                    </div>
                </div>
            </section>

            <!-- How It Works Section -->
            <section class="py-12 sm:py-16 lg:py-20 bg-blue-dark text-white" aria-labelledby="how-it-works-headline">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <!-- Header -->
                    <header class="text-center mb-12 lg:mb-16">
                        <h2 id="how-it-works-headline" class="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                            {{ texts.howItWorks.headline }}
                        </h2>
                        <p class="max-w-2xl mx-auto text-sm sm:text-base text-white/80">
                            {{ texts.howItWorks.subcopy }}
                        </p>
                    </header>

                    <!-- Steps -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        <article
                            v-for="(step, index) in texts.howItWorks.steps"
                            :key="index"
                            class="relative flex flex-col items-center text-center bg-white/5 border border-white/10 rounded-2xl px-6 py-8 shadow-lg shadow-black/20 backdrop-blur-sm hover:bg-white/10 hover:-translate-y-1 transition-transform transition-colors duration-200"
                        >
                            <div v-if="index === 3" class="mb-4">
                                <img class="w-16" :src="$asset('hkcm/images/present.png', 'public')" alt="HKCM Present" title="HKCM Present" />
                            </div>

                            <div v-else class="inline-flex items-center justify-center w-14 h-14 mb-4 rounded-full bg-white text-blue-dark text-2xl font-extrabold shadow-md">
                                {{ index + 1 }}
                            </div>

                            <h3 class="text-lg sm:text-xl font-semibold mb-2">
                                {{ step.title }}
                            </h3>

                            <p class="text-sm sm:text-base text-white/80">
                                {{ step.description }}
                            </p>
                        </article>
                    </div>
                </div>
            </section>
            <!-- FAQ Section -->
            <section class="py-12 sm:py-16 lg:py-20" aria-labelledby="faq-headline">
                <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <header class="text-center mb-10 sm:mb-12">
                        <h2 class="text-2xl">
                            {{ texts.faq.headline }}
                        </h2>
                        <p class="text-gray-600">
                            {{ texts.faq.subcopy }}
                        </p>
                    </header>

                    <div class="rounded-lg overflow-hidden">
                        <Accordeon v-for="(item, index) in texts.faq.items" :key="index" :content="item.answer" :title="item.question" />
                    </div>
                </div>
            </section>
            <!--Section to Reedem Gift-->
            <section class="py-12 sm:py-16 lg:py-20 bg-blue-dark text-white" aria-labelledby="redeem-headline">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 id="redeem-headline" class="text-2xl font-bold mb-3">Geschenk einlösen</h2>
                    <p class="max-w-3xl mx-auto mb-4">
                        Haben Sie einen Geschenkcode erhalten? Lösen Sie ihn jetzt ein und genießen Sie den Zugang zu unseren professionellen Marktanalysen.
                    </p>
                    <button type="button" class="btn primary flex flex-row gap-2 items-center max-w-3xl mx-auto" @click="showRedeemPopup = true">
                        Geschenkcode einlösen
                        <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </button>
                </div>
            </section>
        </main>

        <!-- Success Modal -->
        <transition name="modal">
            <teleport to="body">
                <Popup :open="submitSuccess" :padding="true" @open="submitSuccess = !submitSuccess" @close="submitSuccess">
                    <div class="bg-white w-full p-6 sm:p-8 animate-slideUp">
                        <!-- Success Icon -->
                        <div class="text-center mb-6">
                            <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" aria-hidden="true">
                                <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 id="success-modal-title" class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Geschenk erfolgreich gekauft!</h2>
                            <p class="text-gray-600">Ihr Geschenkcode wurde generiert</p>
                        </div>

                        <!-- Voucher Code -->
                        <div class="bg-gray-50 rounded-lg p-6 mb-6 border-2 border-dashed border-gray-300">
                            <p class="text-sm text-gray-600 mb-2 text-center">Ihr Geschenkcode</p>
                            <p class="text-2xl sm:text-3xl font-mono font-bold text-center text-blue-600 tracking-wider break-all" aria-label="Geschenkcode">
                                {{ voucherCode }}
                            </p>
                        </div>

                        <!-- Actions -->
                        <div class="space-y-3">
                            <button
                                type="button"
                                class="w-full px-6 py-3 bg-blue text-white font-semibold rounded-md hover:bg-blue focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                @click="copyVoucherCode"
                            >
                                <svg class="inline-block w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                </svg>
                                {{ copyButtonText }}
                            </button>

                            <button
                                type="button"
                                class="w-full px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
                                @click="printVoucher"
                            >
                                <svg class="inline-block w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                    />
                                </svg>
                                Als PDF speichern/Drucken
                            </button>

                            <button
                                type="button"
                                class="w-full px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
                                @click="router.push('/')"
                            >
                                Zur Startseite
                            </button>
                        </div>
                    </div>
                </Popup>
            </teleport>
        </transition>

        <!-- Popup Selected Gift-->
        <Popup :open="popupOpen" :padding="true" @open="popupOpen = !popupOpen" @close="closePopup">
            <div class="max-w-3xl mx-auto p-0 mt-6 lg:mt-0 lg:p-4">
                <h2 class="text-2xl font-bold mb-4 text-center">Ausgewähltes Geschenk</h2>
                <div class="bg-white rounded-lg">
                    <ProductCard :product="selectedProduct" :show-variants="true" :show-price="true" @buy="(data) => handleSuccess(data)" />
                </div>
            </div>
        </Popup>

        <!--Reedem Popop-->
        <Popup :open="showRedeemPopup" :padding="true" @open="showRedeemPopup = !showRedeemPopup" @close="closePopup">
            <GiftRedeem />
        </Popup>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ProductCard from './ProductCard.vue';
import ProductGroupDirect from './product/ProductGroupDirect.vue';
import ProductCategorySingleMid from './product/ProductCategorySingleMid.vue';
import Accordeon from './miscellaneous/Accordeon.vue';
import Popup from './miscellaneous/Popup.vue';
import Loading from './miscellaneous/Loading.vue';
import Login from './auth/Login.vue';
import GiftRedeem from './GiftRedeem.vue';
import { createGiftVoucherHtml } from '../createGiftVoucherHtml.js';

const router = useRouter();
const configuratorSection = ref(null);
const copyButtonText = ref('Code kopieren');
const store = useStore();
const popupOpen = ref(false);
const loading = ref(true);
const region = computed(() => window.meta?.region_configuration?.region || 'de');
const loggedIn = computed(() => store.getters['user/isLoggedIn']);
const showLoginButton = ref(false);
const showRedeemPopup = ref(false);
const voucherCode = ref('');
const submitSuccess = ref(false);
const selectedProduct = ref(null);

const texts = {
    brand: 'GiftShop',
    header: {
        cta: 'Jetzt verschenken'
    },
    hero: {
        headline: 'Ein Geschenk für die Zukunft',
        subcopy: 'Machen Sie einem besonderen Menschen eine Freude und schenken Sie ihm Zugang zu professionellen Marktanalysen – ein Geschenk mit nachhaltigem Wert.',
        cta: 'Jetzt verschenken'
    },
    configurator: {
        headline: 'Wählen Sie Ihr Geschenk',
        subcopy: 'Entdecken Sie unsere Pakete und personalisieren Sie Ihr Geschenk Nachricht',
        giftToggle: {
            label: 'Als Geschenk kaufen',
            description: 'Aktivieren Sie diese Option, um das Produkt als Geschenk zu versenden'
        }
    },
    howItWorks: {
        headline: "So funktioniert's",
        subcopy: 'In drei einfachen Schritten zum perfekten Geschenk',
        steps: [
            {
                title: 'Produkt auswählen',
                description: 'Wählen Sie aus unseren hochwertigen Paketen das passende Geschenk aus'
            },
            {
                title: 'Personalisieren',
                description: 'Individualisieren Sie Ihr Geschenk, indem Sie eine persönliche Nachricht hinzufügen '
            },
            {
                title: 'Verschenken',
                description: 'Geschenkcode direkt weitergeben – oder ihn mit unserer Vorlage als Geschenkgutschein ausdrucken'
            },
            {
                title: 'Einlösen',
                description: 'Der Beschenkte kann den Geschenkcode einfach auf unserer Website einlösen und sofort loslegen'
            }
        ]
    },
    faq: {
        headline: 'Häufig gestellte Fragen',
        subcopy: 'Alles, was Sie über unsere Geschenke wissen müssen',
        items: [
            {
                question: 'Wie lange ist der Geschenkcode gültig?',
                answer: 'Der Geschenkcode ist ab Ausstellung 12 Monate lang gültig und kann jederzeit innerhalb dieses Zeitraums eingelöst werden.'
            },
            {
                question: 'Kann ich die Zustellung des Geschenks planen?',
                answer: 'Ja! Bei der E-Mail-Zustellung können Sie ein Datum in der Zukunft auswählen. Der Empfänger erhält den Geschenkcode automatisch am gewählten Tag.'
            },
            {
                question: 'Was passiert, wenn der Code verloren geht?',
                answer: 'Kein Problem! Kontaktieren Sie unseren Support mit Ihrer Bestellnummer, und wir senden Ihnen den Code erneut zu.'
            },
            {
                question: 'Kann der Empfänger das Geschenk umtauschen?',
                answer: 'Der Empfänger kann den Geschenkcode gegen jedes Produkt mit gleichem oder geringerem Wert eintauschen. Bei höherem Wert zahlt er einfach die Differenz.'
            }
        ]
    }
};

watch(loggedIn, (newVal) => {
    loggedIn.value = newVal;
    showLoginButton.value = !loggedIn.value || region.value !== 'de';
});

onMounted(async () => {
    if (region.value !== 'de') {
        await router.push('/');
    }

    if (!loggedIn.value || region.value !== 'de') {
        showLoginButton.value = true;
    } else {
        showLoginButton.value = false;
        productCategories();
    }

    if (window.location.hash === '#redeem') {
        showRedeemPopup.value = true;
    }
});

const closePopup = () => {
    popupOpen.value = false;
};

/**
 *
 */

const shrink = (expandId) => {
    const instance = getCurrentInstance && getCurrentInstance();
    const productGroupRefs = instance?.proxy?.$refs?.productGroupDirect || [];
    productGroupRefs.forEach((productGroup) => {
        if (productGroup?.data?.id !== expandId && typeof productGroup.shrink === 'function') {
            productGroup.shrink();
        }
    });
};

/**
 * Holt Produktkategorien
 *
 */

const productCategories = () => {
    const productCategories = store.getters['product_categories/get']();

    if (!productCategories || productCategories.length === 0) {
        loading.value = true;
        store.dispatch('product_categories/index').then(async () => {
            loading.value = false;
        });
    }

    loading.value = false;

    return productCategories;
};

/**
 * Holt Produktgruppe nach ProductCategoryId
 *
 */

const getProductGroupByProductCategoryId = (productCategoryId) => {
    return store.getters['product_groups/getByProductCategoryId'](productCategoryId);
};
/**
 * Scrollt zum Konfigurator
 */
const scrollToConfigurator = () => {
    configuratorSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Handler für Produkt
 */
const handleProductSelect = (payload) => {
    // Sicherstellen, dass der Payload gültig ist
    if (!payload || !payload.length) {
        console.error('handleProductSelect wurde mit einem ungültigen Payload aufgerufen.');
        return;
    }

    const writtenPayload = payload[0];
    const webinarPayload = payload.length > 1 ? payload[1] : null;

    selectedProduct.value = {
        id: writtenPayload.id,
        price: (writtenPayload.total_price_gross ?? 0) - (writtenPayload.discount ?? 0),
        name: writtenPayload.product?.name,
        image: writtenPayload.product?.image,
        period: writtenPayload.productAttributeValues?.data?.[0]?.title,
        webinar: webinarPayload
            ? {
                  id: webinarPayload.id,
                  name: webinarPayload.product?.name,
                  date: webinarPayload.productAttributeValues?.data?.[0]?.title,
                  price: (webinarPayload.total_price_gross ?? 0) - (webinarPayload.discount ?? 0)
              }
            : null
    };

    popupOpen.value = true;
};

/**
 * Handler für Formular-Submit
 */
const handleSuccess = async (data) => {
    voucherCode.value = data?.code;
    closePopup();
    submitSuccess.value = true;
};

/**
 * Kopiert Voucher-Code in Zwischenablage
 */
const copyVoucherCode = async () => {
    const resetText = () => {
        clearTimeout(resetText._t);
        resetText._t = setTimeout(() => {
            copyButtonText.value = 'Code kopieren';
        }, 2000);
    };

    try {
        await navigator.clipboard.writeText(voucherCode.value);
    } catch (err) {
        console.error('Copy failed:', err);

        const textArea = document.createElement('textarea');
        textArea.value = voucherCode.value;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    } finally {
        copyButtonText.value = '✓ Kopiert!';
        resetText();
    }
};

/**
 * Druckt Voucher
 */
const printVoucher = () => {
    const code = voucherCode.value ?? '';
    const product = selectedProduct.value ?? {};

    const html = createGiftVoucherHtml({
        code,
        productName: product?.name?.de ?? 'Produkt',
        productPeriod: product?.period?.de ?? ''
    });

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
        console.error('Popup-Blocker verhindert das Drucken.');
        return;
    }

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
    };
};
</script>

<style scoped>
.modal-enter-active .animate-slideUp,
.modal-leave-active .animate-slideUp {
    transition: transform 0.3s ease;
}

.modal-enter-from .animate-slideUp {
    transform: translateY(20px);
}

.modal-leave-to .animate-slideUp {
    transform: translateY(20px);
}

/**
 * Print Styles
 */
@media print {
    header,
    footer,
    .no-print {
        display: none !important;
    }
}
</style>
